/**
 * Pull the 11-character video id out of anything a teacher might paste: a
 * watch URL, a youtu.be link, a Shorts link, an embed URL, or the bare id.
 */
export function parseYouTubeId(input: string): string | null {
  const s = input.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  try {
    const url = new URL(s.startsWith('http') ? s : `https://${s}`);
    const host = url.hostname.replace(/^www\.|^m\./, '');
    let id: string | null = null;
    if (host === 'youtu.be') id = url.pathname.slice(1);
    else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      id = url.searchParams.get('v');
      if (!id) {
        const m = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
        id = m?.[1] ?? null;
      }
    }
    id = id?.slice(0, 11) ?? null;
    return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export const thumbnailUrl = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// ── The IFrame Player API, loaded once ──────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type YT = any;
declare global {
  interface Window { YT?: YT; onYouTubeIframeAPIReady?: () => void }
}

let loading: Promise<YT> | null = null;

export function loadYouTubeApi(): Promise<YT> {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.onerror = () => { loading = null; reject(new Error('Could not load YouTube.')); };
    document.head.appendChild(tag);
  });
  return loading;
}

// ── Will it play here? ──────────────────────────────────────────────────────
//
// A channel can switch off "allow embedding", and then the video plays only on
// youtube.com — the student sees "Video unavailable" and nothing is tracked.
// The teacher must find out BEFORE assigning it, so the form asks two things:
//
//   1. oEmbed, for the title (and a fast "no": it answers 401 for videos whose
//      embedding is off, 404 for private or deleted ones).
//   2. A hidden, muted player that actually tries to start the video. Error 101
//      or 150 is YouTube saying "not on other sites" — the same check the
//      student's player would fail, so it is the one that is authoritative.

export type EmbedCheck = 'ok' | 'blocked' | 'missing' | 'unknown';

export async function fetchVideoTitle(id: string): Promise<{ title: string | null; status: number }> {
  try {
    const res = await fetch(
      `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(`https://www.youtube.com/watch?v=${id}`)}`,
    );
    if (!res.ok) return { title: null, status: res.status };
    const data = await res.json();
    return { title: typeof data?.title === 'string' ? data.title : null, status: 200 };
  } catch {
    return { title: null, status: 0 };
  }
}

export async function checkEmbeddable(id: string, timeoutMs = 10_000): Promise<EmbedCheck> {
  const YT = await loadYouTubeApi().catch(() => null);
  if (!YT) return 'unknown';

  return new Promise(resolve => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;left:-9999px;top:0;width:200px;height:120px;opacity:0;pointer-events:none';
    const mount = document.createElement('div');
    host.appendChild(mount);
    document.body.appendChild(host);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let player: any = null;
    let settled = false;
    const finish = (r: EmbedCheck) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try { player?.destroy(); } catch { /* gone */ }
      host.remove();
      resolve(r);
    };
    const timer = window.setTimeout(() => finish('unknown'), timeoutMs);

    player = new YT.Player(mount, {
      videoId: id,
      width: 200,
      height: 120,
      playerVars: { autoplay: 1, mute: 1, controls: 0, playsinline: 1 },
      events: {
        onReady: (e: { target: { mute: () => void; playVideo: () => void } }) => {
          e.target.mute();
          e.target.playVideo();
        },
        onStateChange: (e: { data: number }) => {
          if (e.data === YT.PlayerState.PLAYING || e.data === YT.PlayerState.BUFFERING) finish('ok');
        },
        onError: (e: { data: number }) => {
          finish(e.data === 101 || e.data === 150 ? 'blocked' : e.data === 100 ? 'missing' : 'unknown');
        },
      },
    });
  });
}
